import PocketBase from 'pocketbase';
import { get } from 'svelte/store';
import tenant from '@/store/tenant.js';

function getPocketBaseUrl() {
    const hostname = window.location.hostname
    if (hostname.endsWith('.lvh.me') || hostname === 'lvh.me') {
        return 'http://lvh.me:8090/'
    }
    if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.localhost')) {
        return 'http://localhost:8090/'
    }
    return `${window.location.origin}/`
}
export const url = getPocketBaseUrl();
export const client = new PocketBase(url);
client.autoCancellation(false);

const TENANT_EXCLUDED_COLLECTIONS = [
    'tenants', 
    'invitations', 
    '_superusers', 
    'users',
    'userVisits',
    'viewStrains',
    'viewInventory',
    'viewOrders',
    'bestsellers',
    'tv_link'
];

function getTenantId() {
    return get(tenant)?.id || null;
}

function shouldFilterByTenant(col) {
    return !TENANT_EXCLUDED_COLLECTIONS.includes(col) && getTenantId() !== null;
}

function addTenantFilter(existingFilter) {
    const tenantId = getTenantId();
    if (!tenantId) return existingFilter;
    
    const tenantFilter = `tenant = "${tenantId}"`;
    if (!existingFilter) return tenantFilter;
    return `(${existingFilter}) && ${tenantFilter}`;
}

function injectTenantId(data) {
    const tenantId = getTenantId();
    if (!tenantId) return data;
    return { ...data, tenant: tenantId };
}

export async function getDocs(col) {
    try {
        const options = { sort: '-created' };
        if (shouldFilterByTenant(col)) {
            options.filter = addTenantFilter(null);
        }
        return await client.collection(col).getFullList(options); 
    } catch (e) { 
        console.log('FAIL to get documents:', e); 
        throw e;
    }
}
export async function addDoc(col, data) {
    try {
        const docData = shouldFilterByTenant(col) ? injectTenantId(data) : data;
        return await client.collection(col).create(docData);
    } catch (e) {
        console.log('FAIL to create document:', e);
        throw e;
    }
}
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Create many records without tripping PocketBase's rate limiter.
// Runs with small concurrency and a gap between requests; on a 429 it backs
// off and retries that record. Returns the created records in input order.
// onProgress(done, total) is called after each successful create.
export async function createMany(col, items, { concurrency = 2, gapMs = 90, retries = 5, onProgress } = {}) {
    const results = new Array(items.length);
    let next = 0;
    let done = 0;

    async function worker() {
        while (next < items.length) {
            const idx = next++;
            const data = shouldFilterByTenant(col) ? injectTenantId(items[idx]) : items[idx];
            for (let attempt = 0; ; attempt++) {
                try {
                    results[idx] = await client.collection(col).create(data);
                    break;
                } catch (e) {
                    if (e?.status === 429 && attempt < retries) {
                        await sleep(Math.min(4000, 400 * 2 ** attempt) + Math.random() * 250);
                        continue;
                    }
                    throw e;
                }
            }
            done++;
            if (onProgress) onProgress(done, items.length);
            if (gapMs) await sleep(gapMs);
        }
    }

    await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, worker));
    return results;
}

export async function delDoc(col, id) {
    try {
        return await client.collection(col).delete(id);
    } catch (e) {
        console.log('FAIL to delete document:', e);
        throw e;
    }
}
export async function getId(col, id) {
    try { 
        return await client.collection(col).getOne(id); 
    } catch (e) { 
        console.log('FAIL to get document:', e); 
        throw e;
    }
}
export async function updateDoc(col, id, data) {
    try {
        return await client.collection(col).update(id, data);
    } catch (e) {
        console.log('FAIL to update document:', e);
        throw e;
    }
}
export async function getDocsLimit(col, limit, sort, page = 1) {
    try {
        const options = { sort };
        if (shouldFilterByTenant(col)) {
            options.filter = addTenantFilter(null);
        }
        let res = await client.collection(col).getList(page, limit, options);
        return res.items;
    } catch (e) { console.log('FAIL') }
}
export async function getDocsFiltered(col, filter = null, sort = '-created', limit = 200, page = 1) {
    try {
        const options = { sort };
        const combined = shouldFilterByTenant(col) ? addTenantFilter(filter) : filter;
        if (combined) options.filter = combined;
        const res = await client.collection(col).getList(page, limit, options);
        return res.items;
    } catch (e) {
        console.error('FAIL to get filtered documents:', e);
        throw e;
    }
}
export async function getDocsSinceWeeks(col, weeks = 1, sort = '-created', page = 1, limit = 500) {
    const now = new Date();
    const pastDate = new Date(now);
    pastDate.setDate(now.getDate() - (weeks * 7));

    let filter = `created >= "${pastDate.toISOString()}"`;
    if (shouldFilterByTenant(col)) {
        filter = addTenantFilter(filter);
    }

    try {
        const res = await client.collection(col).getList(page, limit, { filter, sort });
        return res.items;
    } catch (e) {
        console.log('FAIL');
    }
}

export async function getDocsSinceWeeksAll(col, weeks = 1, sort = '-created') {
    const now = new Date();
    const pastDate = new Date(now);
    pastDate.setDate(now.getDate() - (weeks * 7));

    let filter = `created >= "${pastDate.toISOString()}"`;
    if (shouldFilterByTenant(col)) {
        filter = addTenantFilter(filter);
    }

    try {
        return await client.collection(col).getFullList({ filter, sort });
    } catch (e) {
        console.log('FAIL to get all documents since weeks:', e);
        throw e;
    }
}
export async function getDocsPaginated(col, page = 1, perPage = 10, sort = '-created') {
    try {
        const options = { sort };
        if (shouldFilterByTenant(col)) {
            options.filter = addTenantFilter(null);
        }
        const result = await client.collection(col).getList(page, perPage, options);
        return {
            items: result.items,
            page: result.page,
            perPage: result.perPage,
            totalItems: result.totalItems,
            totalPages: result.totalPages,
            hasMore: result.page < result.totalPages
        };
    } catch (e) {
        console.error('Failed to get paginated documents:', e);
        throw e;
    }
}

export async function searchDocsPaginated(col, searchTerm, page = 1, perPage = 10, searchableFields = [], extraClauses = []) {
    try {
        if (searchableFields.length === 0) {
            const sample = await client.collection(col).getList(1, 1);
            if (sample.items.length > 0) {
                const sampleRecord = sample.items[0];
                const keys = Object.keys(sampleRecord).filter(key => !key.startsWith('@') && !['id', 'collectionId', 'collectionName', 'created', 'updated', 'image', 'certificate', 'tenant'].includes(key));
                searchableFields = keys.filter(key => {
                    const value = sampleRecord[key];
                    return typeof value === 'string' || Array.isArray(value) || (value && typeof value === 'object');
                });
            }
        }

        const searchWords = searchTerm.trim().toLowerCase().split(/\s+/);
        
        const wordFilters = searchWords.map(word => {
            const escapedWord = word.replace(/"/g, '\\"');
            const fieldConditions = searchableFields.map(field => {
                return `${field} ~ "${escapedWord}"`;
            }).join(' || ');
            return `(${fieldConditions})`;
        });
        
        let filter = wordFilters.join(' && ');
        // Extra clauses (e.g. sales matched by their linked customer's current
        // name) are OR'd with the field search so a term matches either the
        // record's own fields or the resolved relation.
        if (extraClauses && extraClauses.length) {
            filter = `(${filter}) || (${extraClauses.join(' || ')})`;
        }
        if (shouldFilterByTenant(col)) {
            filter = addTenantFilter(filter);
        }

        const result = await client.collection(col).getList(page, perPage, {
            filter,
            sort: '-created'
        });
        
        return {
            items: result.items,
            page: result.page,
            perPage: result.perPage,
            totalItems: result.totalItems,
            totalPages: result.totalPages,
            hasMore: result.page < result.totalPages
        };
    } catch (e) {
        console.error('PocketBase paginated search failed:', e);
        console.log('Falling back to client-side search...');
        
        try {
            const allRecords = await getDocs(col);
            const searchWords = searchTerm.trim().toLowerCase().split(/\s+/);
            
            const filtered = allRecords.filter(record => {
                const recordString = JSON.stringify(Object.values(record)).toLowerCase();
                return searchWords.every(word => recordString.includes(word));
            });
            
            const startIndex = (page - 1) * perPage;
            const endIndex = startIndex + perPage;
            const paginatedItems = filtered.slice(startIndex, endIndex);
            
            return {
                items: paginatedItems,
                page: page,
                perPage: perPage,
                totalItems: filtered.length,
                totalPages: Math.ceil(filtered.length / perPage),
                hasMore: endIndex < filtered.length
            };
        } catch (fallbackError) {
            console.error('Client-side search also failed:', fallbackError);
            throw fallbackError;
        }
    }
}

export async function searchDocs(col, searchTerm, searchableFields = []) {
    if (!searchTerm || !searchTerm.trim()) {
        return await getDocs(col);
    }

    try {
        const allRecords = await getDocs(col);
        
        if (!allRecords || allRecords.length === 0) {
            return allRecords;
        }

        let fieldsToSearch = searchableFields;
        if (fieldsToSearch.length === 0) {
            const sampleRecord = allRecords[0];
            fieldsToSearch = Object.keys(sampleRecord).filter(key => {
                const value = sampleRecord[key];
                return typeof value === 'string' && 
                       !key.startsWith('@') && 
                       !['id', 'collectionId', 'collectionName', 'created', 'updated', 'image', 'certificate', 'tenant'].includes(key);
            });
        }

        const searchWords = searchTerm.trim().toLowerCase().split(/\s+/);
        
        const wordFilters = searchWords.map(word => {
            const fieldConditions = fieldsToSearch.map(field => {
                const escapedWord = word.replace(/"/g, '\\"');
                return `${field} ~ "${escapedWord}"`;
            }).join(' || ');
            
            return `(${fieldConditions})`;
        });
        
        let filter = wordFilters.join(' && ');
        if (shouldFilterByTenant(col)) {
            filter = addTenantFilter(filter);
        }
        
        return await client.collection(col).getFullList({ 
            filter,
            sort: '-created' 
        });
    } catch (e) {
        console.error('PocketBase search failed:', e);
        console.log('Falling back to client-side search...');
        
        try {
            const allRecords = await getDocs(col);
            const searchWords = searchTerm.trim().toLowerCase().split(/\s+/);
            
            return allRecords.filter(record => {
                const recordString = JSON.stringify(Object.values(record)).toLowerCase();
                return searchWords.every(word => recordString.includes(word));
            });
        } catch (fallbackError) {
            console.error('Client-side search also failed:', fallbackError);
            return await getDocs(col);
        }
    }
}

const FILE_COLLECTION_BY_VIEW = {
    viewStrains: 'strains',
    viewInventory: 'inventory',
};

export function imgUrl(data, noImage, thumb) {
    if (!data?.image) return noImage;
    let size = thumb ? '?thumb=0x300' : '';
    const fileCollection =
        FILE_COLLECTION_BY_VIEW[data.collectionName] ||
        FILE_COLLECTION_BY_VIEW[data['@collectionName']] ||
        data.collectionId;
    return `${url}api/files/${fileCollection}/${data.id}/${data.image}${size}`;
}

export function getFileUrl(data, fieldName = 'image') {
    if (!data?.[fieldName]) return null;
    return `${url}api/files/${data.collectionId}/${data.id}/${data[fieldName]}`;
}

export function getFileType(filename) {
    if (!filename) return 'unknown';
    const extension = filename.toLowerCase().split('.').pop();
    const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'];
    const pdfTypes = ['pdf'];
    
    if (imageTypes.includes(extension)) return 'image';
    if (pdfTypes.includes(extension)) return 'pdf';
    return 'unknown';
}

export function isPdfFile(filename) {
    return getFileType(filename) === 'pdf';
}

export async function getPublicViewByTenant(col) {
    const tenantId = get(tenant)?.id || null;
    const params = { perPage: 500 };
    if (tenantId) {
        params.tenant = tenantId;
    }
    try {
        const all = [];
        let page = 1;
        let totalPages = 1;
        do {
            const result = await client.send(`/api/collections/${col}/records`, {
                method: 'GET',
                params: { ...params, page }
            });
            if (Array.isArray(result?.items)) {
                all.push(...result.items);
            }
            totalPages = result?.totalPages || 1;
            page++;
        } while (page <= totalPages);
        return all;
    } catch (e) {
        console.error('Failed to load public view by tenant:', col, e);
        throw e;
    }
}

export async function getPublicViewRecord(col, id) {
    const tenantId = get(tenant)?.id || null;
    const params = {};
    if (tenantId) {
        params.tenant = tenantId;
    }
    try {
        return await client.send(`/api/collections/${col}/records/${id}`, {
            method: 'GET',
            params,
        });
    } catch (e) {
        console.error('Failed to load public view record:', col, id, e);
        throw e;
    }
}